import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/CombinedVoiceTweetSection.css';

function CombinedVoiceTweetSection() {
  const { t, i18n } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioFiles, setAudioFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedAudios, setLoadedAudios] = useState({}); // 用于缓存已加载的音频
  const audioRef = useRef(null);
  const prevPlayedIndices = useRef([]); // 记录已播放的音频索引，避免短时间内重复播放
  
  // 停止正在播放的音频
  const stopCurrentAudio = () => {
    if (audioRef.current) {
      // 移除所有事件监听器
      const audio = audioRef.current;
      audio.oncanplaythrough = null;
      audio.onended = null;
      audio.onerror = null;
      
      // 停止播放
      audio.pause();
      audio.currentTime = 0;
    }
  };

  // 获取中文音频文件
  const getChineseAudioFiles = () => {
    // 使用最新的一组中文音频文件
    return [
      'tiny_chinese_1_1746871499.mp3',
      'tiny_chinese_2_1746871503.mp3',
      'tiny_chinese_3_1746871506.mp3',
      'tiny_chinese_4_1746871509.mp3',
      'tiny_chinese_5_1746871512.mp3',
      'tiny_chinese_6_1746871515.mp3',
      'tiny_chinese_7_1746871518.mp3',
      'tiny_chinese_8_1746871521.mp3',
      'tiny_chinese_9_1746871524.mp3',
      'tiny_chinese_10_1746871527.mp3',
      'tiny_chinese_11_1746871530.mp3',
      'tiny_chinese_12_1746871533.mp3',
      'tiny_chinese_13_1746871536.mp3',
      'tiny_chinese_14_1746871539.mp3',
      'tiny_chinese_15_1746871542.mp3',
      'tiny_chinese_16_1746871545.mp3',
      'tiny_chinese_17_1746871549.mp3',
      'tiny_chinese_18_1746871551.mp3',
      'tiny_chinese_19_1746871554.mp3',
      'tiny_chinese_20_1746871557.mp3',
      'tiny_chinese_21_1746871560.mp3',
    ];
  };

  // 获取英文音频文件（按情绪分类）
  const getEnglishAudioFiles = () => {
    return [
      // happy类型
      'tiny_english_happy_1_1746876518.mp3',
      'tiny_english_happy_2_1746876522.mp3',
      'tiny_english_happy_3_1746876524.mp3',
      'tiny_english_happy_4_1746876528.mp3',
      'tiny_english_happy_5_1746876531.mp3',
      // loving类型
      'tiny_english_loving_6_1746876534.mp3',
      'tiny_english_loving_7_1746876537.mp3',
      'tiny_english_loving_8_1746876540.mp3',
      'tiny_english_loving_9_1746876543.mp3',
      'tiny_english_loving_10_1746876546.mp3',
      // angry类型
      'tiny_english_angry_11_1746876549.mp3',
      'tiny_english_angry_12_1746876552.mp3',
      'tiny_english_angry_13_1746876556.mp3',
      'tiny_english_angry_14_1746876559.mp3',
      'tiny_english_angry_15_1746876566.mp3',
      // surprised类型
      'tiny_english_surprised_16_1746876569.mp3',
      'tiny_english_surprised_17_1746876572.mp3',
      'tiny_english_surprised_18_1746876575.mp3',
      'tiny_english_surprised_19_1746876580.mp3',
      'tiny_english_surprised_20_1746876583.mp3',
      // sad类型
      'tiny_english_sad_21_1746876586.mp3',
      'tiny_english_sad_22_1746876589.mp3',
      'tiny_english_sad_23_1746876592.mp3',
      'tiny_english_sad_24_1746876595.mp3',
      'tiny_english_sad_25_1746876598.mp3',
    ];
  };

  // 根据当前语言加载对应的音频文件列表
  useEffect(() => {
    const fetchAudioFiles = async () => {
      try {
        // 确保停止任何可能正在播放的音频
        stopCurrentAudio();
        setIsPlaying(false);
        setIsLoading(false);
        
        const lang = i18n.language;
        const folderPath = lang === 'zh' ? '/audios/cute_sounds/' : '/audios/cute_sounds_english/';
        
        // 根据当前语言设置音频文件
        if (lang === 'zh') {
          setAudioFiles(getChineseAudioFiles().map(file => `${folderPath}${file}`));
        } else {
          setAudioFiles(getEnglishAudioFiles().map(file => `${folderPath}${file}`));
        }

        // 清除之前语言的已加载音频缓存
        // 先释放所有已加载音频资源
        Object.values(loadedAudios).forEach(audio => {
          audio.oncanplaythrough = null;
          audio.onended = null;
          audio.onerror = null;
          audio.pause();
          audio.src = '';
        });
        
        setLoadedAudios({});
        prevPlayedIndices.current = [];
      } catch (error) {
        console.error('获取音频文件列表失败', error);
      }
    };

    fetchAudioFiles();
    
    // 组件卸载时清理所有音频资源
    return () => {
      stopCurrentAudio();
      
      // 释放所有已加载音频资源
      Object.values(loadedAudios).forEach(audio => {
        audio.oncanplaythrough = null;
        audio.onended = null;
        audio.onerror = null;
        audio.pause();
        audio.src = '';
      });
    };
  }, [i18n.language]);

  // 预加载部分音频文件
  useEffect(() => {
    if (audioFiles.length === 0) return;
    
    // 预加载前3个音频文件
    const preloadCount = Math.min(3, audioFiles.length);
    
    for (let i = 0; i < preloadCount; i++) {
      preloadAudio(audioFiles[i]);
    }
    
    // 清理函数
    return () => {
      // 释放已加载的音频资源
      Object.values(loadedAudios).forEach(audio => {
        audio.oncanplaythrough = null;
        audio.onended = null;
        audio.onerror = null;
        audio.pause();
        audio.src = '';
      });
      
      stopCurrentAudio();
    };
  }, [audioFiles]);

  // 预加载音频文件
  const preloadAudio = (audioSrc) => {
    if (loadedAudios[audioSrc]) return; // 如果已经加载过，则跳过
    
    const audio = new Audio();
    audio.src = audioSrc;
    audio.preload = 'auto';
    
    // 监听加载完成事件
    const handleCanPlayThrough = () => {
      // 音频加载完成，将其添加到缓存中
      setLoadedAudios(prev => ({
        ...prev,
        [audioSrc]: audio
      }));
      
      // 移除事件监听器，避免内存泄漏
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
    };
    
    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    
    // 加载音频
    audio.load();
  };

  // 获取一个未最近播放过的随机索引
  const getRandomNonRepeatingIndex = () => {
    if (audioFiles.length <= 1) return 0;
    
    // 如果已经播放了超过一半的音频，清空历史记录
    if (prevPlayedIndices.current.length >= audioFiles.length / 2) {
      prevPlayedIndices.current = [];
    }
    
    // 生成还未播放过的音频索引列表
    const availableIndices = Array.from(
      { length: audioFiles.length }, 
      (_, i) => i
    ).filter(index => !prevPlayedIndices.current.includes(index));
    
    // 如果所有音频都已播放过，返回随机索引
    if (availableIndices.length === 0) {
      return Math.floor(Math.random() * audioFiles.length);
    }
    
    // 从未播放列表中随机选择一个索引
    const randomIndex = Math.floor(Math.random() * availableIndices.length);
    return availableIndices[randomIndex];
  };

  // 播放随机音频
  const playRandomAudio = () => {
    // 如果正在播放或加载中，则不执行
    if (isPlaying || isLoading || audioFiles.length === 0) return;
    
    // 设置加载中状态
    setIsLoading(true);
    
    // 确保停止任何可能正在播放的音频
    stopCurrentAudio();
    
    // 获取一个不重复的随机索引
    const randomIndex = getRandomNonRepeatingIndex();
    const selectedAudioFile = audioFiles[randomIndex];
    
    // 记录播放过的索引
    prevPlayedIndices.current.push(randomIndex);
    
    let audio;
    
    // 检查音频是否已缓存
    if (loadedAudios[selectedAudioFile]) {
      // 使用已缓存的音频
      audio = loadedAudios[selectedAudioFile];
      audio.currentTime = 0;
      
      // 立即播放
      setIsLoading(false);
      setIsPlaying(true);
      
      // 确保移除之前可能存在的事件监听器
      audio.onended = null;
      
      // 添加结束事件监听器
      audio.onended = () => {
        setIsPlaying(false);
        audioRef.current = null;
        
        // 预加载下一个音频
        const nextIndex = (randomIndex + 1) % audioFiles.length;
        if (!loadedAudios[audioFiles[nextIndex]]) {
          preloadAudio(audioFiles[nextIndex]);
        }
      };
      
      audio.play().catch(error => {
        console.error('播放缓存音频失败', error);
        setIsPlaying(false);
        setIsLoading(false);
        audioRef.current = null;
      });
    } else {
      // 否则创建新的音频实例
      audio = new Audio(selectedAudioFile);
      
      // 移除可能存在的事件监听器
      audio.oncanplaythrough = null;
      audio.onended = null;
      audio.onerror = null;
      
      // 监听音频加载完成事件
      audio.oncanplaythrough = () => {
        setIsLoading(false);
        setIsPlaying(true);
        
        // 确保移除之前可能存在的事件监听器
        audio.onended = null;
        
        // 添加结束事件监听器
        audio.onended = () => {
          setIsPlaying(false);
          audioRef.current = null;
          
          // 预加载下一个音频
          const nextIndex = (randomIndex + 1) % audioFiles.length;
          if (!loadedAudios[audioFiles[nextIndex]]) {
            preloadAudio(audioFiles[nextIndex]);
          }
        };
        
        audio.play();
        
        // 缓存该音频
        setLoadedAudios(prev => ({
          ...prev,
          [selectedAudioFile]: audio
        }));
      };
      
      // 监听音频加载失败事件
      audio.onerror = () => {
        console.error('音频加载失败', selectedAudioFile);
        setIsLoading(false);
        setIsPlaying(false);
        audioRef.current = null;
      };
      
      // 加载音频
      audio.load();
    }
    
    // 保存当前音频引用
    audioRef.current = audio;
  };

  // 处理键盘事件
  const handleKeyDown = (event) => {
    // 如果正在播放或加载中，则不执行
    if (isPlaying || isLoading || audioFiles.length === 0) return;
    
    // 当按下空格键或回车键时触发播放
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      playRandomAudio();
    }
  };

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      stopCurrentAudio();
      
      // 释放所有已加载音频资源
      Object.values(loadedAudios).forEach(audio => {
        audio.oncanplaythrough = null;
        audio.onended = null;
        audio.onerror = null;
        audio.pause();
        audio.src = '';
      });
    };
  }, []);

  return (
    <section className="combined-section">
      <div className="combined-container">
        <div className="voice-content">
          <div className="bobo-image-container">
            <img src="/images/page5/body.png" alt="BOBO AI pet" className="bobo-image" />
          </div>
        </div>
      </div>
      
      {/* 播放按钮 - 移至section直接子元素，通过绝对定位控制位置 */}
      <div 
        className={`play-button-container ${(isPlaying || isLoading) ? 'disabled' : ''}`} 
        onClick={playRandomAudio}
        onKeyDown={handleKeyDown}
        aria-label={isPlaying ? t('common.pauseAudio') : t('common.playAudio')}
        role="button"
        tabIndex={(isPlaying || isLoading) ? -1 : 0}
        aria-disabled={isPlaying || isLoading}
      >
        <img 
          src="/images/page7.png" 
          alt="Play button" 
          className={`play-button ${isPlaying ? 'playing' : ''} ${isLoading ? 'loading' : ''}`}
        />
      </div>
    </section>
  );
}

export default CombinedVoiceTweetSection; 